import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Clear,
  Search,
  ShowChart,
  StackedLineChart,
  VpnLockOutlined,
} from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import dayjs from "dayjs";
import DatagridComponent from "../../../../../components/datagridComponent";
import LineChartComponent from "../../../../../components/lineComponent";
import NoHostScreen from "../../noHostScreen";
import useStyles from "./styles";

const timeZone = "America/Sao_Paulo";

// Texto pré-quebrado: é desenhado dentro do SVG da ilustração, que não quebra
// linha sozinho (máx. ~28 caracteres por linha)
const SEM_DADOS_VPN = `Este equipamento não possui\nVPNs configuradas.`;

// Modos de exibição do gráfico (igual ao painel de Links do BGP)
const CHART_MODE = {
  STACKED: "stacked",
  MULTILINE: "multiline",
};

export default function FortigateVpnPanel({ vpnListUrl = "/zabbix/dash/vpnList" } = {}) {
  const { api, filterData, selectTime, optionsTime, handleAplicarZoom } =
    useOutletContext();
  const theme = useTheme();
  const styles = useStyles(theme);

  // Estado dos dados retornados pelo endpoint otimizado
  const [data, setData] = useState({ vpns: [], trafficIn: {}, trafficOut: {} });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [chartMode, setChartMode] = useState(CHART_MODE.STACKED);

  // AbortController PRÓPRIO — não compartilha com o GenericDashboard
  const controllerRef = useRef(null);

  // Paleta cíclica determinística para as linhas dos gráficos
  const colors = [
    theme?.palette?.colors?.red?.light,
    theme?.palette?.colors?.orange?.light,
    theme?.palette?.colors?.yellow?.light,
    theme?.palette?.colors?.green?.light,
    theme?.palette?.colors?.blue?.light,
    theme?.palette?.colors?.purple?.light,
    theme?.palette?.colors?.pink?.light,
    theme?.palette?.colors?.red?.medium,
    theme?.palette?.colors?.orange?.medium,
    theme?.palette?.colors?.yellow?.medium,
    theme?.palette?.colors?.green?.medium,
    theme?.palette?.colors?.blue?.medium,
    theme?.palette?.colors?.purple?.medium,
    theme?.palette?.colors?.pink?.medium,
  ];

  // Busca a lista de VPNs quando host/identificadores/janela mudam
  useEffect(() => {
    const EMPTY_DATA = { vpns: [], trafficIn: {}, trafficOut: {} };

    // Todos os campos obrigatórios devem estar preenchidos para consultar;
    // caso contrário, limpa os dados imediatamente e não faz a requisição.
    const allFieldsFilled =
      filterData?.hostId &&
      filterData?.fabricante &&
      filterData?.funcao &&
      filterData?.modelo &&
      filterData?.clienteId &&
      filterData?.serviceId;

    if (!allFieldsFilled) {
      // Aborta qualquer requisição em andamento e limpa o painel
      if (controllerRef.current) controllerRef.current.abort();
      setData(EMPTY_DATA);
      setLoading(false);
      return;
    }

    // Calcula a janela de tempo (igual ao dashSfp)
    let dateInitial = filterData?.initialDate;
    let finalDate = filterData?.finalDate;
    if (selectTime || selectTime === 0) {
      dateInitial = optionsTime?.[selectTime]?.value;
      finalDate = dayjs()?.set("second", 0)?.set("millisecond", 0)?.toDate();
    }

    // Aborta requisição anterior do painel
    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    // Limpa dados antes do fetch para não exibir resultados antigos durante o carregamento
    setData(EMPTY_DATA);
    setLoading(true);

    const fetchVpns = async () => {
      try {
        const response = await api.post(
          vpnListUrl,
          {
            hostids: filterData?.hostId,
            initial_date: dateInitial,
            final_date: finalDate,
            fabricante: filterData?.fabricante,
            funcao: filterData?.funcao,
            modelo: filterData?.modelo,
            dash: "fortigate",
          },
          { signal: controller.signal },
        );
        if (!controller.signal.aborted) {
          setData(response?.data || EMPTY_DATA);
        }
      } catch (error) {
        if (!controller.signal.aborted) console.error(error);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchVpns();
  }, [
    filterData?.hostId,
    filterData?.fabricante,
    filterData?.funcao,
    filterData?.modelo,
    filterData?.clienteId,
    filterData?.serviceId,
    filterData?.initialDate,
    filterData?.finalDate,
    selectTime,
  ]);

  // Aborta a requisição ao desmontar
  useEffect(() => {
    return () => {
      if (controllerRef.current) controllerRef.current.abort();
    };
  }, []);

  // Seleciona todas as VPNs ao carregar a lista
  useEffect(() => {
    if (data?.vpns?.length > 0) {
      setSelectedIds(data?.vpns?.map((vpn) => vpn?.id));
    } else {
      setSelectedIds([]);
    }
  }, [data?.vpns]);

  // Mapa id -> nome do túnel (para legenda/cores dos gráficos)
  const nameById = useMemo(() => {
    return (data?.vpns || []).reduce((acc, vpn) => {
      acc[vpn?.id] = vpn?.name || vpn?.id;
      return acc;
    }, {});
  }, [data?.vpns]);

  // Mapa nome -> cor (determinístico pela ordem da lista)
  const colorByName = useMemo(() => {
    return (data?.vpns || []).reduce((acc, vpn, idx) => {
      acc[vpn?.name || vpn?.id] = colors[idx % colors.length];
      return acc;
    }, {});
  }, [data?.vpns, colors]);

  // Linhas da tabela
  const rows = (data?.vpns || []).map((vpn) => ({
    id: vpn?.id,
    name: vpn?.name || vpn?.id,
    status: vpn?.status,
    uptime: vpn?.uptime,
    trafficInLast: vpn?.trafficInLast,
    trafficOutLast: vpn?.trafficOutLast,
  }));

  // Filtragem por texto de busca
  const filteredRows = rows.filter((row) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return Object.values(row).some((value) =>
      String(value).toLowerCase().includes(searchLower),
    );
  });

  // Colunas: Nome, Status, Uptime, Entrada (último), Saída (último)
  const columns = [
    { field: "id", headerName: "ID", hide: true },
    {
      field: "name",
      headerName: "VPN",
      flex: 2.5,
      renderCell: (params) => (
        <Typography
          variant="body2"
          noWrap
          title={params?.value}
          sx={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params?.value}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const isUp = Number(params?.value) === 2;
        return (
          <Chip
            label={isUp ? "UP" : "DOWN"}
            color={isUp ? "success" : "error"}
            size="small"
            sx={{ height: 18, "& .MuiChip-label": { py: 0 } }}
          />
        );
      },
    },
    {
      field: "uptime",
      headerName: "Uptime",
      flex: 1.2,
      renderCell: (params) => {
        const text = formatUptime(params?.value);
        return (
          <Typography variant="body2" noWrap title={text}>
            {text}
          </Typography>
        );
      },
    },
    {
      field: "trafficInLast",
      headerName: "Entrada",
      flex: 1,
      renderCell: (params) => {
        const title = formatBytes({ bytes: params?.value, precisao: 2 });
        return (
          <Typography variant="body2" noWrap title={title}>
            {formatBytes({ bytes: params?.value, precisao: 0 })}
          </Typography>
        );
      },
    },
    {
      field: "trafficOutLast",
      headerName: "Saída",
      flex: 1,
      renderCell: (params) => {
        const title = formatBytes({ bytes: params?.value, precisao: 2 });
        return (
          <Typography variant="body2" noWrap title={title}>
            {formatBytes({ bytes: params?.value, precisao: 0 })}
          </Typography>
        );
      },
    },
  ];

  // Trata seleção de linhas (mantém apenas ids)
  const handleSelectionChange = (selectionModel) => {
    setSelectedIds(selectionModel || []);
  };

  // Configuração dos dois gráficos (entrada/saída)
  const graphs = [
    {
      key: "trafficIn",
      title: "Tráfego de entrada",
      titleColor: theme?.palette?.cardGraphic?.graphColor?.blue,
    },
    {
      key: "trafficOut",
      title: "Tráfego de saída",
      titleColor: theme?.palette?.cardGraphic?.graphColor?.orange,
    },
  ];

  // Chaves (nomes) das VPNs selecionadas, para os modos stacked/multiline
  const selectedNames = selectedIds?.map((id) => nameById?.[id] || id);
  const objectColorsLine =
    chartMode === CHART_MODE.MULTILINE
      ? selectedNames.reduce((acc, name) => {
          acc[name] = colorByName?.[name];
          return acc;
        }, {})
      : false;

  const toggleChartMode = () => {
    setChartMode((prev) =>
      prev === CHART_MODE.STACKED ? CHART_MODE.MULTILINE : CHART_MODE.STACKED,
    );
  };

  // Device sem VPN mapeada: schema de VPN ausente ou nenhuma VPN no Zabbix
  if (!loading && (!data?.vpns || data?.vpns?.length === 0)) {
    return <NoHostScreen mensagem={SEM_DADOS_VPN} />;
  }

  return (
    <Box sx={styles.container}>
      <Grid container spacing={1}>
        {/* Tabela de VPNs */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", ...styles.gridContent }}
        >
          <Paper
            elevation={theme.palette.mode === "dark" ? 0 : 3}
            sx={styles.paperContent}
          >
            {!!loading && <LinearProgress sx={styles.linearProgress} />}
            <Box sx={styles.boxTitle}>
              <Box sx={styles.titleIcon}>
                <VpnLockOutlined color="primary" />
                <Typography>VPNs</Typography>
              </Box>
              <TextField
                size="small"
                variant="outlined"
                label="Buscar"
                placeholder="Buscar VPN"
                value={search || ""}
                disabled={loading}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearch("")}
                        aria-label="Limpar busca"
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>
            <Divider sx={{ mt: 1 }} />
            <Box sx={{ ...styles.paperContent }}>
              <DatagridComponent
                columns={columns}
                rows={filteredRows}
                loading={loading}
                options={{
                  checkboxSelection: true,
                  onRowSelectionModelChange: handleSelectionChange,
                  rowSelectionModel: selectedIds,
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Gráficos de tráfego das VPNs selecionadas */}
        <Grid item xs={12} md={6} sx={styles.gridContent}>
          <Paper
            elevation={theme.palette.mode === "dark" ? 0 : 3}
            sx={{
              ...styles.paperContent,
              flexDirection: "column",
              p: 2,
              gap: 1,
            }}
          >
            {!!loading && <LinearProgress sx={styles.linearProgress} />}
            {graphs?.map((graph, idx) => {
              // Monta os datasets das VPNs selecionadas (history keyed por id, label por nome)
              const datasets = selectedIds.map((id) => ({
                key: nameById?.[id] || id,
                data: data?.[graph.key]?.[id]?.history || [],
              }));
              const chartData = concatenateStackedData(datasets);

              // Cores na ordem real das chaves do gráfico
              const actualKeysOrder = chartData.length
                ? Object.keys(chartData[0]).filter((k) => k !== "clock")
                : [];
              const stackedGraphColors = actualKeysOrder.map(
                (key) => colorByName?.[key],
              );

              return (
                <Paper
                  key={graph.key}
                  variant="outlined"
                  sx={styles.paperGraphcs}
                >
                  <Box sx={styles.boxTitleGraphcs}>
                    <Typography color={graph?.titleColor} variant="subtitle1">
                      {graph?.title}
                    </Typography>
                    {/* Alterna entre soma empilhada e linhas separadas */}
                    {idx === 0 && (
                      <Tooltip
                        title={
                          chartMode === CHART_MODE.STACKED
                            ? "Mostrar linhas separadas"
                            : "Mostrar valores somados"
                        }
                      >
                        <IconButton
                          onClick={toggleChartMode}
                          size="small"
                          sx={styles.toggleViewButton}
                        >
                          {chartMode === CHART_MODE.STACKED ? (
                            <ShowChart fontSize="small" />
                          ) : (
                            <StackedLineChart fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <LineChartComponent
                    data={chartData}
                    isZoomEnabled={true}
                    onZoomChange={(range) => {
                      const startDate = dayjs.unix(range?.start).tz(timeZone);
                      const endDate = dayjs.unix(range?.end).tz(timeZone);
                      handleAplicarZoom(startDate, endDate);
                    }}
                    stackedGraphKeys={
                      chartMode === CHART_MODE.STACKED ? selectedNames : []
                    }
                    stackedGraphColors={stackedGraphColors}
                    objectColorsLine={
                      chartMode === CHART_MODE.MULTILINE
                        ? objectColorsLine
                        : null
                    }
                    lineChartOptions={{
                      margin: { top: 20, right: 30, left: 10, bottom: 0 },
                    }}
                    xOptions={{
                      tickFormatter: (clock) =>
                        dayjs.unix(clock).tz(timeZone).format("DD/MM HH:mm"),
                    }}
                  />
                </Paper>
              );
            })}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// Normaliza datasets por clock para o gráfico (uma chave por VPN selecionada)
function concatenateStackedData(datasets) {
  const map = new Map();
  datasets.forEach(({ key, data }) => {
    data?.forEach(({ clock, value }) => {
      if (!map.has(clock)) map.set(clock, { clock });
      map.get(clock)[key] = value;
    });
  });
  return Array.from(map.values()).sort((a, b) => a.clock - b.clock);
}

// Formata bytes/s em bps/Kbps/Mbps...
const formatBytes = ({ bytes, unidade = null, precisao = 2 }) => {
  if (bytes === 0 || bytes === "0") return "0 bps";
  if (bytes == null || isNaN(Number(bytes))) return " - ";
  const k = 1000;
  const sizes =
    unidade === "pps"
      ? ["pps", "Kpps", "Mpps", "Gpps", "Tpps", "Ppps", "Epps"]
      : ["bps", "Kbps", "Mbps", "Gbps", "Tbps", "Pbps", "Ebps"];
  const i = Math?.floor(Math?.log(Number(bytes)) / Math?.log(k));
  return (
    parseFloat((Number(bytes) / Math?.pow(k, i))?.toFixed(precisao)) +
    " " +
    sizes[i]
  );
};

// Formata uptime em segundos para "Xd Yh Zm"
const formatUptime = (seconds) => {
  const total = Number(seconds);
  if (seconds == null || isNaN(total) || total < 0) return " - ";
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(" ");
};
